import { useEffect, useRef } from 'react';
import type { Token, TokenSnapshot, SidebarTabId, UndoableCommand, ClientMessage } from '../types';
import { useShortcutContext } from '../contexts/ShortcutContext';
import { useToolContext } from '../contexts/ToolContext';
import { createTokenDeleteCommand, createBatchCommand } from '../lib/undo-commands';
import { ZOOM_STEP } from '../utils';
import type { DistributiveOmit } from './useMultiplayer';

/** Deps for useAppShortcuts — mirrors the values available in AppShell. */
interface AppShortcutsDeps {
  setSnapToGrid: (fn: (prev: boolean) => boolean) => void;
  setShortcutsOpen: (fn: (prev: boolean) => boolean) => void;
  engine: {
    camera: { zoom: number };
    setZoom: (zoom: number) => void;
    fitToView: () => void;
    gridSize: number;
    markAllDirty: () => void;
  };
  tokens: {
    tokens: Token[];
    selectedToken: Token | null;
    selectedTokenIds: ReadonlySet<string>;
    removeToken: (id: string) => void;
    addTokenDirect: (t: Token) => void;
    selectToken: (id: string) => void;
    setTokens: (tokens: Token[]) => void;
    copyToClipboard?: (tokenId: string, pageId: string) => void;
  };
  initiative: {
    next: () => void;
  };
  /** Multiplayer — accepts the full UseMultiplayerReturn type */
  mp: { session: unknown; send: (msg: DistributiveOmit<ClientMessage, 'id' | 'timestamp'>) => void };
  isGM: boolean;
  outgoingPageId: string;
  sidebarTabs: {
    setActiveTab: (tab: SidebarTabId) => void;
  };
  isFullscreen: boolean;
  exitFullscreen?: () => void;
  undoStack?: {
    push: (cmd: UndoableCommand) => void;
    undo: () => void;
    redo: () => void;
  };
  toggleSidebarCollapsed?: () => void;
  fog?: {
    brushSize: { current: number };
    setBrushSize: (size: number) => void;
  };
  currentTool: string;
  setFogBrushSize?: (size: number) => void;
  drawToolOptions?: {
    drawWidth: number;
    setOption: (key: 'drawWidth', value: number) => void;
  };
  textHook?: {
    selectedTextId: string | null;
    deleteText: (id: string) => void;
    setSelectedTextId: (id: string | null) => void;
  };
  handlePasteFromClipboard?: () => void;
  handleDeleteText?: (textId: string) => void;
  sendCreateRef?: React.RefObject<(token: TokenSnapshot) => void>;
  sendDeleteRef?: React.RefObject<(tokenId: string) => void>;
  toggleInspector?: () => void;
  toggleQuickAccess?: () => void;
  toggleInitiative?: () => void;
  /** True while a canvas ruler (measure tool / token drag) owns the Q key for
   * waypoints — suppresses the quick-access toggle so Q doesn't double-fire. */
  isRulerActive?: () => boolean;
}

/**
 * Registers all app-level keyboard shortcuts into the ShortcutContext registry.
 * Actions read from a ref so closures are always fresh without re-registering.
 */
export function useAppShortcuts(deps: AppShortcutsDeps): void {
  const registry = useShortcutContext();
  const { setTool } = useToolContext();

  // Keep deps in a ref so action closures always read fresh values
  const d = useRef(deps);
  useEffect(() => { d.current = deps; });

  useEffect(() => {
    const unregisters: (() => void)[] = [];
    const reg = (entry: Parameters<typeof registry.register>[0]) => {
      unregisters.push(registry.register(entry));
    };

    // === Tool shortcuts ===
    reg({ id: 'tool:select', keys: 'S', label: 'shortcuts.toolSelect', category: 'tool', action: () => setTool('select') });
    reg({ id: 'tool:select-drawing', keys: 'V', label: 'shortcuts.toolSelectDrawing', category: 'tool', action: () => setTool('select-drawing'), enabled: () => d.current.isGM });
    reg({ id: 'tool:draw', keys: 'B', label: 'shortcuts.toolDraw', category: 'tool', action: () => setTool('draw') });
    reg({ id: 'tool:erase', keys: 'E', label: 'shortcuts.toolEraser', category: 'tool', action: () => setTool('erase') });
    reg({ id: 'tool:measure', keys: 'R', label: 'shortcuts.toolRuler', category: 'tool', action: () => setTool('measure') });
    reg({ id: 'tool:fog', keys: 'F', label: 'shortcuts.toolFog', category: 'tool', action: () => setTool('fog') });
    reg({ id: 'tool:note', keys: 'N', label: 'shortcuts.toolNote', category: 'tool', action: () => { if (d.current.isGM) setTool('note'); }, enabled: () => d.current.isGM });
    reg({ id: 'tool:location-link', keys: 'L', label: 'shortcuts.toolPortal', category: 'tool', action: () => { if (d.current.isGM) setTool('location-link'); }, enabled: () => d.current.isGM });
    reg({ id: 'tool:text', keys: 'T', label: 'shortcuts.toolText', category: 'tool', action: () => { if (d.current.isGM) setTool('text'); }, enabled: () => d.current.isGM });
    reg({ id: 'tool:aoe', keys: 'A', label: 'shortcuts.toolAoe', category: 'tool', action: () => { if (d.current.isGM) setTool('aoe'); }, enabled: () => d.current.isGM });
    reg({ id: 'tool:map-object', keys: 'O', label: 'shortcuts.toolMapObject', category: 'tool', action: () => { if (d.current.isGM) setTool('map-object'); }, enabled: () => d.current.isGM });

    // === Canvas shortcuts ===
    reg({ id: 'canvas:snap-grid', keys: 'G', label: 'shortcuts.snapToggle', category: 'canvas', action: () => d.current.setSnapToGrid((p: boolean) => !p) });
    reg({ id: 'canvas:zoom-in', keys: '+', label: 'shortcuts.zoomIn', category: 'canvas', action: () => d.current.engine.setZoom(d.current.engine.camera.zoom * ZOOM_STEP) });
    reg({ id: 'canvas:zoom-in-eq', keys: '=', label: 'shortcuts.zoomIn', category: 'canvas', action: () => d.current.engine.setZoom(d.current.engine.camera.zoom * ZOOM_STEP) });
    reg({ id: 'canvas:zoom-out', keys: '-', label: 'shortcuts.zoomOut', category: 'canvas', action: () => d.current.engine.setZoom(d.current.engine.camera.zoom / ZOOM_STEP) });
    reg({ id: 'canvas:fit', keys: '0', label: 'shortcuts.fitToScreen', category: 'canvas', action: () => d.current.engine.fitToView() });
    reg({
      id: 'canvas:space',
      keys: 'Space',
      label: 'shortcuts.nextTurn',
      category: 'canvas',
      action: () => d.current.initiative.next(),
    });
    reg({
      id: 'canvas:fog-brush-dec',
      keys: '[',
      label: 'shortcuts.fogBrushDec',
      category: 'canvas',
      action: () => {
        const { fog, currentTool, setFogBrushSize, drawToolOptions } = d.current;
        if (fog && currentTool === 'fog') {
          const newSize = Math.max(20, fog.brushSize.current - 10);
          fog.setBrushSize(newSize);
          if (setFogBrushSize) setFogBrushSize(newSize);
        } else if ((currentTool === 'draw' || currentTool === 'erase' || currentTool === 'shape') && drawToolOptions) {
          const newWidth = Math.max(1, drawToolOptions.drawWidth - 1);
          drawToolOptions.setOption('drawWidth', newWidth);
        }
      },
    });
    reg({
      id: 'canvas:fog-brush-inc',
      keys: ']',
      label: 'shortcuts.fogBrushInc',
      category: 'canvas',
      action: () => {
        const { fog, currentTool, setFogBrushSize, drawToolOptions } = d.current;
        if (fog && currentTool === 'fog') {
          const newSize = Math.min(300, fog.brushSize.current + 10);
          fog.setBrushSize(newSize);
          if (setFogBrushSize) setFogBrushSize(newSize);
        } else if ((currentTool === 'draw' || currentTool === 'erase' || currentTool === 'shape') && drawToolOptions) {
          const newWidth = Math.min(20, drawToolOptions.drawWidth + 1);
          drawToolOptions.setOption('drawWidth', newWidth);
        }
      },
    });
    reg({
      id: 'canvas:delete',
      keys: 'Delete',
      label: 'shortcuts.deleteTokens',
      category: 'canvas',
      action: () => {
        const { textHook, handleDeleteText, tokens, mp, outgoingPageId, undoStack, sendCreateRef, sendDeleteRef } = d.current;
        // Delete selected text object if text tool active
        if (textHook?.selectedTextId) {
          if (handleDeleteText) {
            handleDeleteText(textHook.selectedTextId);
          } else {
            textHook.deleteText(textHook.selectedTextId);
            textHook.setSelectedTextId(null);
          }
          return;
        }
        // token:delete is GM-only on the server — do not remove locally and send a
        // command that will be refused (players hit Delete, saw nothing, pressed F5).
        if (tokens.selectedTokenIds.size > 0 && d.current.isGM) {
          const commands: UndoableCommand[] = [];
          for (const id of tokens.selectedTokenIds) {
            const tok = tokens.tokens.find((t) => t.id === id);
            if (!tok) continue;
            const { imageEl: _img, ...cleanSnapshot } = tok;
            tokens.removeToken(id);
            if (mp.session) mp.send({ type: 'token:delete', tokenId: id, pageId: outgoingPageId });
            commands.push(createTokenDeleteCommand({
              token: cleanSnapshot,
              addToken: (t: Token) => tokens.addTokenDirect(t),
              removeToken: (rid: string) => tokens.removeToken(rid),
              sendCreate: (t: TokenSnapshot) => { if (sendCreateRef) sendCreateRef.current(t); },
              sendDelete: (tid: string) => { if (sendDeleteRef) sendDeleteRef.current(tid); },
            }));
          }
          if (commands.length === 1 && undoStack) {
            undoStack.push(commands[0]);
          } else if (commands.length > 1 && undoStack) {
            undoStack.push(createBatchCommand(commands, commands.map((c) => c.description).join(', ')));
          }
        }
      },
    });
    reg({
      id: 'canvas:backspace',
      keys: 'Backspace',
      label: 'shortcuts.deleteTokens',
      category: 'canvas',
      action: () => {
        // Same as Delete — trigger the delete shortcut
        registry.trigger('Delete');
      },
    });

    // === Token quick select (1-9) ===
    for (let digit = 1; digit <= 9; digit++) {
      const n = digit;
      reg({
        id: `canvas:select-token-${n}`,
        keys: String(n),
        label: 'shortcuts.quickSelectToken',
        category: 'canvas',
        action: () => {
          const t = d.current.tokens.tokens[n - 1];
          if (t) d.current.tokens.selectToken(t.id);
        },
      });
    }

    // === Sidebar shortcuts ===
    reg({ id: 'sidebar:toggle', keys: 'Ctrl+B', label: 'shortcuts.toggleSidebar', category: 'sidebar', action: () => { d.current.toggleSidebarCollapsed?.(); } });
    reg({ id: 'sidebar:map-panel', keys: 'D', label: 'shortcuts.openMapPanel', category: 'sidebar', action: () => d.current.sidebarTabs.setActiveTab('map') });
    reg({ id: 'sidebar:inspector', keys: 'Ctrl+I', label: 'shortcuts.toggleInspector', category: 'sidebar', action: () => { d.current.toggleInspector?.(); } });
    reg({ id: 'sidebar:initiative', keys: 'I', label: 'shortcuts.toggleInitiative', category: 'sidebar', action: () => { d.current.toggleInitiative?.(); } });
    reg({ id: 'sidebar:quickAccess', keys: 'Q', label: 'shortcuts.toggleQuickAccess', category: 'sidebar', enabled: () => !(d.current.isRulerActive?.() ?? false), action: () => { d.current.toggleQuickAccess?.(); } });

    // === Global shortcuts ===
    reg({ id: 'global:shortcuts', keys: 'Ctrl+/', label: 'shortcuts.showShortcuts', category: 'global', action: () => d.current.setShortcutsOpen((p: boolean) => !p) });
    reg({
      id: 'global:copy',
      keys: 'Ctrl+C',
      label: 'shortcuts.copy',
      category: 'global',
      action: () => {
        const { isGM, tokens, outgoingPageId } = d.current;
        if (isGM && tokens.selectedToken && tokens.copyToClipboard) {
          tokens.copyToClipboard(tokens.selectedToken.id, outgoingPageId);
        }
      },
    });
    reg({
      id: 'global:paste',
      keys: 'Ctrl+V',
      label: 'shortcuts.paste',
      category: 'global',
      action: () => {
        if (d.current.isGM && d.current.handlePasteFromClipboard) d.current.handlePasteFromClipboard();
      },
    });
    reg({ id: 'global:undo', keys: 'Ctrl+Z', label: 'shortcuts.undo', category: 'global', action: () => { d.current.undoStack?.undo(); } });
    reg({ id: 'global:redo', keys: 'Ctrl+Shift+Z', label: 'shortcuts.redo', category: 'global', action: () => { d.current.undoStack?.redo(); } });
    reg({
      id: 'global:exit-fullscreen',
      keys: 'Escape',
      label: 'shortcuts.exitFullscreen',
      category: 'global',
      action: () => {
        if (d.current.isFullscreen && d.current.exitFullscreen) d.current.exitFullscreen();
      },
    });

    return () => {
      for (const u of unregisters) u();
    };
  }, [registry, setTool]); // Stable deps only — actions read d.current for fresh values
}
